import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import recallsService from '../../../../../services/recalls.service';
import { useForm } from 'react-hook-form';
import Loading from '../../../../common/Loading';
const MEDIUM_LABELS = {
    EMAIL: 'Email',
    SMS: 'SMS',
    CALL: 'Phone Call',
    PATIENT_PREFERRED: 'Patient Preferred',
};

const formatMedium = (medium) => MEDIUM_LABELS[medium] || medium || 'Not set';

const StatusBadge = ({ isActive }) => (
    <span className={`recalls-status-badge ${isActive ? 'recalls-status-badge--active' : 'recalls-status-badge--inactive'}`}>
        {isActive ? 'Active' : 'Inactive'}
    </span>
);

const SectionTitle = ({ children, first }) => (
    <div className={`recalls-section-title${first ? ' recalls-section-title--first' : ''}`}>{children}</div>
);

const InfoRow = ({ label, children }) => (
    <div className="notifications-info-row">
        <span className="notifications-info-label">{label}</span>
        <div className="notifications-info-value">{children}</div>
    </div>
);

function RecallSettings() {
    const { clinicId } = useParams();
    const [settings, setSettings] = useState(null);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getSettings();
    }, [clinicId]);

    const getSettings = () => {
        setLoading(true);
        recallsService
            .getClinicSettings(clinicId)
            .then((res) => {
                setSettings(res.data);
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                toast.error('Failed to load recall settings');
                setLoading(false);
            });
    };

    const handleUpdateSettings = (newSettings) => {
        setSettings(newSettings);
        setEditing(false);
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-bell"></i>
                    Recall Settings
                </span>
                {settings && (
                    <button
                        type="button"
                        className={`btn btn-sm ${editing ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                        onClick={() => setEditing(!editing)}
                    >
                        <i className={`bi ${editing ? 'bi-x' : 'bi-pencil-square'} me-1`}></i>
                        {editing ? 'Cancel' : 'Edit'}
                    </button>
                )}
            </div>

            <div className="notifications-panel__body">
                {settings &&
                    (editing ? (
                        <RecallEdit settings={settings} updateSettings={handleUpdateSettings} setEditing={setEditing} />
                    ) : (
                        <RecallView settings={settings} />
                    ))}
            </div>
        </div>
    );
}

const RecallView = ({ settings }) => {
    const RecallConfigRow = ({ label, enabled, waitPeriod, medium }) => (
        <InfoRow label={label}>
            <div className="recalls-recall-row">
                <StatusBadge isActive={enabled} />
                {enabled && (
                    <>
                        <span className="text-muted small">After {waitPeriod} days</span>
                        <span className="recalls-medium-badge">{formatMedium(medium)}</span>
                    </>
                )}
            </div>
        </InfoRow>
    );

    return (
        <>
            <InfoRow label="System Status">
                <StatusBadge isActive={settings.isActive} />
            </InfoRow>

            <SectionTitle first>Daily Limits</SectionTitle>
            <div className="recalls-stat-grid mb-2">
                <div className="recalls-stat">
                    <div className="recalls-stat__value">{settings.emailLimitPerDay}</div>
                    <div className="recalls-stat__label">Email / day</div>
                </div>
                <div className="recalls-stat">
                    <div className="recalls-stat__value">{settings.smsLimitPerDay}</div>
                    <div className="recalls-stat__label">SMS / day</div>
                </div>
                <div className="recalls-stat">
                    <div className="recalls-stat__value">{settings.callLimitPerDay}</div>
                    <div className="recalls-stat__label">Calls / day</div>
                </div>
            </div>

            <SectionTitle>Recall Configuration</SectionTitle>
            <RecallConfigRow
                label="Second Recall"
                enabled={settings.isSecondRecall}
                waitPeriod={settings.secondRecallWaitPeriod}
                medium={settings.secondRecallMedium}
            />
            <RecallConfigRow
                label="Third Recall"
                enabled={settings.isThirdRecall}
                waitPeriod={settings.thirdRecallWaitPeriod}
                medium={settings.thirdRecallMedium}
            />
            <RecallConfigRow
                label="Fourth Recall"
                enabled={settings.isFourthRecall}
                waitPeriod={settings.fourthRecallWaitPeriod}
                medium={settings.fourthRecallMedium}
            />
        </>
    );
};

const EDIT_TABS = [
    { id: 'general', label: 'General' },
    { id: 'second', label: 'Second Recall' },
    { id: 'third', label: 'Third Recall' },
    { id: 'fourth', label: 'Fourth Recall' },
];

const RecallEdit = ({ settings, updateSettings, setEditing }) => {
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        watch,
        setValue,
    } = useForm({
        defaultValues: {
            id: settings.id,
            clinicId: settings.clinicId,
            isActive: settings.isActive,
            emailLimitPerDay: settings.emailLimitPerDay || 100,
            smsLimitPerDay: settings.smsLimitPerDay || 100,
            callLimitPerDay: settings.callLimitPerDay || 100,
            isSecondRecall: settings.isSecondRecall,
            secondRecallMedium: settings.secondRecallMedium || 'PATIENT_PREFERRED',
            secondRecallWaitPeriod: settings.secondRecallWaitPeriod || 30,
            isThirdRecall: settings.isThirdRecall,
            thirdRecallMedium: settings.thirdRecallMedium || 'PATIENT_PREFERRED',
            thirdRecallWaitPeriod: settings.thirdRecallWaitPeriod || 30,
            isFourthRecall: settings.isFourthRecall,
            fourthRecallMedium: settings.fourthRecallMedium || 'PATIENT_PREFERRED',
            fourthRecallWaitPeriod: settings.fourthRecallWaitPeriod || 30,
        },
    });

    const [activeTab, setActiveTab] = useState('general');

    const isActive = watch('isActive');
    const isSecondRecallEnabled = watch('isSecondRecall');
    const isThirdRecallEnabled = watch('isThirdRecall');
    const isFourthRecallEnabled = watch('isFourthRecall');

    const waitPeriodOptions = [7, 14, 21, 30, 45, 60, 90, 120];
    const mediumOptions = [
        { value: 'EMAIL', label: 'Email' },
        { value: 'SMS', label: 'SMS' },
        { value: 'CALL', label: 'Phone Call' },
        { value: 'PATIENT_PREFERRED', label: 'Patient Preferred' },
    ];

    const onSubmit = async (data) => {
        try {
            const updateData = {
                ...data,
                secondRecallMedium: isSecondRecallEnabled ? data.secondRecallMedium : 'PATIENT_PREFERRED',
                thirdRecallMedium: isThirdRecallEnabled ? data.thirdRecallMedium : 'PATIENT_PREFERRED',
                fourthRecallMedium: isFourthRecallEnabled ? data.fourthRecallMedium : 'PATIENT_PREFERRED',
            };

            const response = await recallsService.saveSettings(data.clinicId, updateData);
            updateSettings(response.data);
            toast.success('Recall settings updated successfully');
        } catch (err) {
            toast.error('Error updating settings. Please try again.');
        }
    };

    const ToggleSwitch = ({ name, checked, onChange, label, hint }) => (
        <div className="recalls-toggle-wrap">
            <div className="form-check form-switch mb-0">
                <input className="form-check-input" type="checkbox" id={name} checked={checked} onChange={onChange} />
                <label className="notifications-form-label mb-0 ms-2" htmlFor={name}>
                    {label}
                </label>
            </div>
            {hint && <div className="notifications-form-hint mt-1">{hint}</div>}
        </div>
    );

    const renderRecallTab = (prefix, enabled, enabledField, waitField, mediumField, waitHint) => (
        <div className="row g-3">
            <div className="col-12">
                <ToggleSwitch
                    name={enabledField}
                    checked={enabled}
                    onChange={(e) => setValue(enabledField, e.target.checked)}
                    label={`Enable ${prefix} Recall`}
                    hint={enabled ? `${prefix} recall is enabled` : `${prefix} recall is disabled`}
                />
            </div>
            {enabled && (
                <>
                    <div className="col-md-6">
                        <label htmlFor={waitField} className="notifications-form-label">
                            Wait Period (Days)
                        </label>
                        <select
                            id={waitField}
                            className={`form-select form-select-sm ${errors[waitField] ? 'is-invalid' : ''}`}
                            {...register(waitField, { required: 'Wait period is required' })}
                        >
                            {waitPeriodOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option} days
                                </option>
                            ))}
                        </select>
                        <div className="notifications-form-hint">{waitHint}</div>
                    </div>
                    <div className="col-md-6">
                        <label htmlFor={mediumField} className="notifications-form-label">
                            Medium
                        </label>
                        <select
                            id={mediumField}
                            className={`form-select form-select-sm ${errors[mediumField] ? 'is-invalid' : ''}`}
                            {...register(mediumField, { required: 'Medium is required' })}
                        >
                            {mediumOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <nav className="recalls-subnav" aria-label="Recall settings sections">
                {EDIT_TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        className={`recalls-subnav__link ${activeTab === tab.id ? 'recalls-subnav__link--active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </nav>

            {activeTab === 'general' && (
                <div className="row g-3">
                    <div className="col-12">
                        <ToggleSwitch
                            name="isActive"
                            checked={isActive}
                            onChange={(e) => setValue('isActive', e.target.checked)}
                            label="Recall System Active"
                            hint={isActive ? 'Recall system is currently active' : 'Recall system is currently inactive'}
                        />
                    </div>
                    <div className="col-12">
                        <SectionTitle first>Daily Limits</SectionTitle>
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="emailLimitPerDay" className="notifications-form-label">
                            Email Limit
                        </label>
                        <input
                            type="number"
                            id="emailLimitPerDay"
                            className={`form-control form-control-sm ${errors.emailLimitPerDay ? 'is-invalid' : ''}`}
                            {...register('emailLimitPerDay', {
                                required: 'Email limit is required',
                                min: { value: 0, message: 'Minimum value is 0' },
                                max: { value: 500, message: 'Maximum value is 500' },
                            })}
                        />
                        <div className="notifications-form-hint">Maximum emails per day</div>
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="smsLimitPerDay" className="notifications-form-label">
                            SMS Limit
                        </label>
                        <input
                            type="number"
                            id="smsLimitPerDay"
                            className={`form-control form-control-sm ${errors.smsLimitPerDay ? 'is-invalid' : ''}`}
                            {...register('smsLimitPerDay', {
                                required: 'SMS limit is required',
                                min: { value: 0, message: 'Minimum value is 0' },
                                max: { value: 500, message: 'Maximum value is 500' },
                            })}
                        />
                        <div className="notifications-form-hint">Maximum SMS per day</div>
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="callLimitPerDay" className="notifications-form-label">
                            Call Limit
                        </label>
                        <input
                            type="number"
                            id="callLimitPerDay"
                            className={`form-control form-control-sm ${errors.callLimitPerDay ? 'is-invalid' : ''}`}
                            {...register('callLimitPerDay', {
                                required: 'Call limit is required',
                                min: { value: 0, message: 'Minimum value is 0' },
                                max: { value: 500, message: 'Maximum value is 500' },
                            })}
                        />
                        <div className="notifications-form-hint">Maximum calls per day</div>
                    </div>
                </div>
            )}

            {activeTab === 'second' &&
                renderRecallTab('Second', isSecondRecallEnabled, 'isSecondRecall', 'secondRecallWaitPeriod', 'secondRecallMedium', 'Days after initial appointment')}
            {activeTab === 'third' &&
                renderRecallTab('Third', isThirdRecallEnabled, 'isThirdRecall', 'thirdRecallWaitPeriod', 'thirdRecallMedium', 'Days after second recall')}
            {activeTab === 'fourth' &&
                renderRecallTab('Fourth', isFourthRecallEnabled, 'isFourthRecall', 'fourthRecallWaitPeriod', 'fourthRecallMedium', 'Days after third recall')}

            <div className="recalls-form-actions">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEditing(false)}>
                    Cancel
                </button>
                <button type="submit" className="btn btn-success btn-sm" disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Updating…
                        </>
                    ) : (
                        <>
                            <i className="bi bi-floppy me-1"></i>
                            Save Settings
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default RecallSettings;
